import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { LimitsBanner } from '@atlassian/jira-servicedesk-cmdb-object-usage-messaging/src/ui/limits-banner/index.tsx';
import { ObjectUsageProvider } from '@atlassian/jira-servicedesk-cmdb-object-usage-messaging/src/utils/store/index.tsx';
import { AsyncLinkedIssuesPanel } from '@atlassian/jira-servicedesk-insight-linked-issues-panel/src/async.tsx';
import { AsyncLinkedObjectsPanel } from '@atlassian/jira-servicedesk-insight-linked-objects-panel/src/async.tsx';
import { AsyncObjectAttachmentsPanel } from '@atlassian/jira-servicedesk-insight-object-attachments-panel/src/async.tsx';
import {
	useObjectStoreState,
	useCanEditObject,
	useCanDeleteObject,
	useCmdbAnalyticAttributes,
} from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { ObjectDetails } from '@atlassian/jira-servicedesk-insight-object-details/src/ui/main.tsx';
import type { ObjectDetailsRenderingContext } from '@atlassian/jira-servicedesk-insight-object-details/src/ui/types.tsx';
import { InsightObjectGraphModal } from '@atlassian/jira-servicedesk-insight-object-graph/src/ui/index.tsx';
import { InsightObjectViewLayout } from '@atlassian/jira-servicedesk-insight-object-view-layout/src/ui/index.tsx';
import {
	toCmdbObjectId,
	toCmdbObjectTypeId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { TrackObjectView } from '@atlassian/jira-servicedesk-insight-user-history-tracker/src/ui/index.tsx';
import { GenericError } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/errors/index.tsx';
import {
	InsightObjectViewHeaderSkeleton,
	InsightObjectViewSkeleton,
} from '@atlassian/jira-skeletons/src/ui/insight-object/InsightObjectViewSkeleton.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import type { BackButtonProps } from '../common/types.tsx';
import { AvatarPanel } from './avatar-panel/index.tsx';
import { ObjectViewHeader } from './header/index.tsx';
import { ObjectDates } from './object-dates/index.tsx';

export type Props = {
	backButton?: BackButtonProps;
	showFullBreadcrumbTrail?: boolean;
	redirectOnDelete: boolean;
	context: ObjectDetailsRenderingContext;
	onClone: () => void;
	onDelete: () => void;
	onFinishLoading?: () => void;
};

export const InsightObjectViewWithoutErrorBoundary = (props: Props) => {
	const [{ objectDetails, objectConfiguration, loading, error }, { setIsCompact }] =
		useObjectStoreState();
	const [canEdit] = useCanEditObject();
	const [canDelete] = useCanDeleteObject();
	const [cmdbUiAnalyticAttributes] = useCmdbAnalyticAttributes();
	const [isObjectGraphOpen, setIsObjectGraphOpen] = useState(false);
	const isSiteAdmin = useIsSiteAdmin();
	const {
		backButton,
		context,
		onClone,
		onDelete,
		redirectOnDelete,
		showFullBreadcrumbTrail,
		onFinishLoading,
	} = props;

	useEffect(() => {
		if (!loading && objectDetails && onFinishLoading !== undefined) {
			onFinishLoading();
		}
	}, [loading, objectDetails, onFinishLoading]);

	if (!objectDetails || loading) {
		return <InsightObjectViewSkeleton />;
	}

	if (error) {
		return <GenericError />;
	}

	const content = (
		<ContextualAnalyticsData
			attributes={{
				...(cmdbUiAnalyticAttributes ?? undefined),
				isSiteAdmin,
			}}
		>
			<InsightObjectViewLayout
				renderHeader={(isMultiColumn: boolean) => (
					<ObjectViewHeader
						canEdit={canEdit}
						canDelete={canDelete}
						backButton={backButton}
						isMultiColumn={isMultiColumn}
						onClone={onClone}
						onDelete={onDelete}
						redirectOnDelete={redirectOnDelete}
						showFullBreadcrumbTrail={showFullBreadcrumbTrail}
					/>
				)}
				renderPrimary={(isMultiColumn: boolean) => (
					<ObjectDetails context={context} isMultiColumn={isMultiColumn} />
				)}
				renderSecondary={(isCompact: boolean) => {
					setIsCompact(isCompact);
					return (
						<SecondaryContent>
							{objectDetails.hasAvatar && <AvatarPanel />}
							<AsyncLinkedObjectsPanel
								onOpenObjectGraph={() => {
									setIsObjectGraphOpen(true);
								}}
							/>
							<AsyncLinkedIssuesPanel />
							<AsyncObjectAttachmentsPanel />
							<ObjectDates />
						</SecondaryContent>
					);
				}}
			/>
			<InsightObjectGraphModal
				objectId={toCmdbObjectId(objectDetails.id)}
				objectTypeId={toCmdbObjectTypeId(objectDetails.objectType.id)}
				source="insight"
				onClose={() => {
					setIsObjectGraphOpen(false);
				}}
				isObjectGraphOpen={isObjectGraphOpen}
			/>

			{objectDetails && <TrackObjectView id={toCmdbObjectId(objectDetails.id)} />}
		</ContextualAnalyticsData>
	);

	if (fg('jsm_assets_cbp_limits_and_notifications')) {
		// When cleaning up this feature flag jsm_assets_cbp_limits_and_notifications combine this check `!objectConfiguration`
		// with the existing `!objectDetails || loading` check above
		if (!objectConfiguration) {
			return <InsightObjectViewHeaderSkeleton />;
		}
		const {
			insightAdministrator,
			limitInfo: { objectCount, objectLimit, billingCycle, limitState = 'disabled' },
		} = objectConfiguration;

		return (
			<ObjectUsageProvider
				isJiraAdmin={insightAdministrator}
				objectCount={objectCount}
				objectLimit={objectLimit}
				billingPeriod={billingCycle === 'annual' ? 'annual' : 'monthly'}
				limitState={limitState}
			>
				<>
					{context === 'Page' ? <LimitsBanner isObjectView /> : null}
					{content}
				</>
			</ObjectUsageProvider>
		);
	}

	return content;
};

export const InsightObjectView = (props: Props) => (
	<JSErrorBoundary id="ObjectView" packageName="jiraServicedeskInsightObjectView" fallback="flag">
		<InsightObjectViewWithoutErrorBoundary {...props} />
	</JSErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SecondaryContent = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300', '24px'),
});
